<template>
  <SbModal
    ref="modal"
    data-testid="modal-delete-caution-confirm"
    :is-open="isOpen"
    :custom-class="modalWrapperClass"
    @hide="closeModal"
  >
    <SbModalHeader
      :data-testid="`${dataTestid}-header`"
      :title="title ?? t('Are you sure?')"
      :align="titleAlign"
    >
      <template v-if="showIcon" #icon>
        <slot name="icon">
          <SbIconCircle
            v-if="iconProps"
            class="modal-delete-caution-confirm__icon"
            :variant="iconProps.variant"
          >
            <SbIcon :name="iconProps.name" />
          </SbIconCircle>
        </slot>
      </template>
    </SbModalHeader>

    <SbModalContent :data-testid="`${dataTestid}-content`">
      <slot name="content" />
    </SbModalContent>

    <div
      v-if="hasButtons"
      :data-testid="`${dataTestid}-footer`"
      :class="modalFooterClass"
    >
      <slot name="footer" />
      <SbButton
        v-if="hasCancelButton"
        :is-disabled="isLoading"
        :label="buttonCancelLabel ?? t('Cancel')"
        type="button"
        variant="secondary"
        :data-testid="`${dataTestid}-cancel-button`"
        @click.stop="handleCancel"
      />
      <SbButton
        v-if="hasActionButton"
        :is-loading="isLoading"
        :label="buttonLabel ?? t('Delete')"
        :is-disabled="buttonDisabled"
        type="button"
        :variant="computedVariant"
        :data-testid="`${dataTestid}-button`"
        @click="handleActionButton"
      />

      <SbLink
        v-if="linkLabel"
        v-safe-href="linkUrl"
        rel="noopener noreferrer"
        target="_blank"
        :data-testid="`${type}-link`"
      >
        <SbButton
          :variant="computedVariant"
          :label="linkLabel"
          :data-testid="`${type}-link-button`"
        />
      </SbLink>
    </div>
  </SbModal>
</template>

<script lang="ts" setup>
/**
 * This modal is meant to be of delete type by default, see props.type
 * but it can also be used as a confirmation, caution, or info modal.
 * Because of that, cancel and action buttons are visible by default but can be hidden via the `has-*-button` props.
 */
import { computed, useAttrs } from 'vue'
import { useI18n } from 'vue-i18n'
import CONFIRMATION_MODAL from './constants'
import {
  SbModal,
  SbModalHeader,
  SbIcon,
  SbModalContent,
  SbButton,
  SbLink,
  SbIconCircle,
} from '@storyblok/design-system'
import type {
  IconCircleVariant,
  IconName,
  AvailableButtonVariants,
} from '@storyblok/design-system'

const props = withDefaults(
  defineProps<{
    isOpen?: boolean
    isLoading?: boolean
    type?: string
    title?: string
    hasButtons?: boolean
    titleAlign?: string
    buttonLabel?: string
    buttonCancelLabel?: string
    buttonDisabled?: boolean
    hasCancelButton?: boolean
    hasActionButton?: boolean
    customClass?: string
    icon?: IconName | null
    hasDefaultFooter?: boolean
    showIcon?: boolean
    linkLabel?: string | null
    linkUrl?: string | null
  }>(),
  {
    isOpen: false,
    isLoading: false,
    type: CONFIRMATION_MODAL.delete.type,
    hasButtons: true,
    titleAlign: 'center',
    buttonDisabled: false,
    hasCancelButton: true,
    hasActionButton: true,
    hasDefaultFooter: false,
    showIcon: true,
  },
)

const emit = defineEmits<{
  (e: 'close-modal'): void
  (e: 'confirm-action'): void
  (e: 'cancel-action'): void
}>()

const { t } = useI18n()
const attrs = useAttrs()

const dataTestid = computed(() => {
  return attrs['data-testid'] || 'base-delete-caution-confirm-modal'
})

const isCautionModal = computed(
  () => props.type === CONFIRMATION_MODAL.caution.type,
)
const isConfirmModal = computed(
  () => props.type === CONFIRMATION_MODAL.confirm.type,
)
const isConfirmNeutralModal = computed(
  () => props.type === CONFIRMATION_MODAL.confirmNeutral.type,
)
const isInfoModal = computed(() => props.type === CONFIRMATION_MODAL.info.type)

const computedVariant = computed<AvailableButtonVariants>(() => {
  if (isConfirmModal.value) {
    return CONFIRMATION_MODAL.confirm.buttonVariant
  } else if (isConfirmNeutralModal.value) {
    return CONFIRMATION_MODAL.confirmNeutral.buttonVariant
  } else if (isCautionModal.value) {
    return CONFIRMATION_MODAL.caution.buttonVariant
  } else if (isInfoModal.value) {
    return CONFIRMATION_MODAL.info.buttonVariant
  }
  return CONFIRMATION_MODAL.delete.buttonVariant
})

const iconProps = computed<
  | undefined
  | {
      name: IconName
      variant: IconCircleVariant
    }
>(() => {
  if (!props.showIcon) {
    return undefined
  }
  if (isConfirmModal.value) {
    return {
      name: props.icon ?? CONFIRMATION_MODAL.confirm.icon,
      variant: CONFIRMATION_MODAL.confirm.iconVariant,
    }
  }
  if (isConfirmNeutralModal.value) {
    return {
      name: props.icon ?? CONFIRMATION_MODAL.confirmNeutral.icon,
      variant: CONFIRMATION_MODAL.confirmNeutral.iconVariant,
    }
  }
  if (isCautionModal.value) {
    return {
      name: props.icon ?? CONFIRMATION_MODAL.caution.icon,
      variant: CONFIRMATION_MODAL.caution.iconVariant,
    }
  }
  if (isInfoModal.value) {
    return {
      name: props.icon ?? CONFIRMATION_MODAL.info.icon,
      variant: CONFIRMATION_MODAL.info.iconVariant,
    }
  }
  return {
    name: props.icon ?? CONFIRMATION_MODAL.delete.icon,
    variant: CONFIRMATION_MODAL.delete.iconVariant,
  }
})

const modalWrapperClass = computed(() => {
  const customClass = 'modal-delete-caution-confirm'
  if (props.customClass) {
    return `${customClass} ${props.customClass}`
  }
  return customClass
})

const modalFooterClass = computed<(string | boolean)[]>(() => {
  return ['modal-footer', props.hasDefaultFooter && 'sb-modal-footer']
})

function closeModal(): void {
  emit('close-modal')
}

function handleActionButton(): void {
  emit('confirm-action')
}

function handleCancel(): void {
  emit('cancel-action')
  closeModal()
}
</script>

<style lang="scss">
@import './modal-delete-caution-confirm';
</style>
